import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useDebounce } from 'use-debounce';
import { animalAction, fazendaAction, logAction } from '../_actions';
import { withRouter } from '../_helpers/withRouter';
import { Button, Container, Row, Col, Card, CardBody, Input, Badge, Modal, ModalHeader, ModalBody, ModalFooter, UncontrolledTooltip, FormGroup, Label, Nav, NavItem, NavLink } from 'reactstrap';
import NavBar from '../_components/navbar';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClock, faPen, faTrash, faPaw, faSearch, faTimes, faChevronLeft, faChevronRight, faVenusMars, faBirthdayCake, faTag, faPlusCircle, faUndo, faHome, faBatteryEmpty, faFolder } from '@fortawesome/free-solid-svg-icons'
import Moment from 'react-moment';
import { ModalLog } from '../log/';
import 'moment-timezone';
import { useNavigate } from 'react-router-dom';

const Animal = (props) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // State
    const [modalHistory, setModalHistory] = useState(false);
    const [selectedFazenda, setSelectedFazenda] = useState('0');
    const [term, setTerm] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(12); // Grid view limit
    const [valueDebounce] = useDebounce(term, 1000);
    const [brincoLog, setBrincoLog] = useState('');
    const [activeTab, setActiveTab] = useState('plantel');

    // Delete Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [animalToDelete, setAnimalToDelete] = useState(null);

    const toggleHistory = () => setModalHistory(!modalHistory);
    const { log } = props.log;
    const { animal = [], totalItems = 0 } = props.animal || {};

    // Effects
    useEffect(() => {
        dispatch(fazendaAction.getFazendaSelect(true));
    }, []);

    useEffect(() => {
        if (selectedFazenda && selectedFazenda !== '0') {
            dispatch(animalAction.getAnimalByFazenda(true, selectedFazenda, page, limit, valueDebounce, activeTab === 'descartados'));
        } else {
            dispatch(animalAction.clear());
        }
    }, [selectedFazenda, page, limit, valueDebounce, activeTab]);

    // Handlers
    const handleChangeTerm = event => {
        setTerm(event.target.value);
        setPage(1);
    };

    const handleSelectFazenda = event => {
        setSelectedFazenda(event.value);
        setPage(1);
        dispatch(fazendaAction.onChangeProps('id_fazenda', { type: 'select', target: { value: event.value } }));
    };

    const confirmDelete = (id) => {
        setAnimalToDelete(id);
        setDeleteModalOpen(true);
    };

    const handleDelete = () => {
        if (animalToDelete) {
            dispatch(animalAction.deleteAnimalById(animalToDelete));
            setDeleteModalOpen(false);
            setAnimalToDelete(null);
            // Refresh list after delete? usually handled by reducer or re-fetch
            const { dispatch } = props;
            dispatch(animalAction.getAnimalByFazenda(true, selectedFazenda, page, limit, valueDebounce, activeTab === 'descartados'));
        }
    };

    const handleReactivar = (id) => {
        dispatch(animalAction.reativarAnimal(id));
    };

    const handleClickHistory = (event, id, brinco) => {
        if (id) {
            setBrincoLog(brinco);
            dispatch(logAction.clear());
            dispatch(logAction.getLog('Animal', id));
            toggleHistory();
        }
    };

    const renderOptionsFazenda = () => {
        return props.fazenda?.fazenda?.map((p) => ({ value: p.id, label: p.nome })) || [];
    };

    // Helpers for Badge Colors and Labels
    const getGrupoLabel = (grupo) => {
        switch (grupo) {
            case 1: return 'Bezerra';
            case 2: return 'Novilha';
            case 3: return 'Lactante';
            case 4: return 'Seca';
            default: return 'Corte';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 1: return 'Vazia';
            case 2: return 'Inseminada';
            case 3: return 'Prenha';
            default: return '-';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 1: return 'secondary'; // Vazia
            case 2: return 'info'; // Inseminada
            case 3: return 'success'; // Prenha
            default: return 'light';
        }
    };

    const totalPages = Math.ceil(totalItems / limit);

    return (
        <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', paddingBottom: '60px' }}>
            <NavBar {...props} />
            <Container className="py-5">

                {/* Header Section */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-5">
                    <div className="mb-3 mb-md-0 text-left">
                        <h2 className="font-weight-bold text-dark mb-1">Animais</h2>
                        {selectedFazenda !== '0' && activeTab !== 'descartados' && (
                            <p className="text-dark font-weight-bold mb-1" style={{ fontSize: '1rem' }}>
                                Total: {totalItems}
                            </p>
                        )}
                        <p className="text-muted mb-0">Gerencie o rebanho da sua fazenda</p>
                    </div>

                    <div className="d-flex flex-column flex-md-row align-items-md-center" style={{ gap: '15px' }}>
                        {/* Farm Selection */}
                        <div style={{ minWidth: '250px' }}>
                            <Select
                                classNamePrefix="select"
                                value={renderOptionsFazenda().filter(opt => opt.value == (selectedFazenda || ''))}
                                onChange={handleSelectFazenda}
                                isSearchable
                                placeholder="Selecione a Fazenda..."
                                options={renderOptionsFazenda()}
                                styles={{
                                    control: (base, state) => ({
                                        ...base,
                                        borderRadius: '50px',
                                        border: state.isFocused ? '1px solid var(--primary-color)' : '1px solid #e0e0e0',
                                        boxShadow: state.isFocused ? '0 0 0 0.2rem var(--primary-shadow)' : '0 2px 4px rgba(0,0,0,0.02)',
                                        paddingLeft: '10px'
                                    })
                                }}
                            />
                        </div>

                        {/* New Animal Button */}
                        <Button
                            id="btnNovo"
                            color="primary"
                            className="btn-rounded px-4 shadow-sm border-0 d-flex align-items-center justify-content-center"
                            style={{ borderRadius: '50px', fontWeight: '600', height: '38px', backgroundColor: 'var(--primary-color)' }}
                            onClick={() => navigate("/animal")}
                        >
                            <FontAwesomeIcon icon={faPlusCircle} className="mr-2" />
                            <span className="text-nowrap">Novo Animal</span>
                        </Button>
                        <UncontrolledTooltip placement="bottom" target="btnNovo">
                            Adicionar novo animal
                        </UncontrolledTooltip>
                    </div>
                </div>

                {/* Filter and Tabs Bar */}
                {selectedFazenda !== '0' && (
                    <Row className="mb-4 align-items-center">
                        <Col md={12}>
                            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
                                <Nav tabs className="border-0 mb-3 mb-md-0 p-1 bg-white shadow-sm rounded-pill" style={{ gap: '5px', width: 'fit-content' }}>
                                    <NavItem>
                                        <NavLink
                                            className={`rounded-pill px-4 border-0 font-weight-bold cursor-pointer d-flex align-items-center justify-content-center ${activeTab === 'plantel' ? 'active text-white shadow-sm' : 'text-muted hover-tab'}`}
                                            onClick={() => { setActiveTab('plantel'); setPage(1); }}
                                            style={{
                                                transition: 'all 0.3s ease',
                                                backgroundColor: activeTab === 'plantel' ? 'var(--primary-color)' : 'transparent',
                                                height: '40px',
                                                minWidth: '120px'
                                            }}
                                        >
                                            Plantel
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={`rounded-pill px-4 border-0 font-weight-bold cursor-pointer d-flex align-items-center justify-content-center ${activeTab === 'descartados' ? 'active text-white shadow-sm' : 'text-muted hover-tab'}`}
                                            onClick={() => { setActiveTab('descartados'); setPage(1); }}
                                            style={{
                                                transition: 'all 0.3s ease',
                                                backgroundColor: activeTab === 'descartados' ? 'var(--primary-color)' : 'transparent',
                                                height: '40px',
                                                minWidth: '120px'
                                            }}
                                        >
                                            Descartados
                                        </NavLink>
                                    </NavItem>
                                </Nav>

                                <div className="position-relative w-100" style={{ maxWidth: '400px' }}>
                                    <Input
                                        id="term"
                                        placeholder="Buscar por brinco, grupo..."
                                        value={term}
                                        onChange={handleChangeTerm}
                                        style={{
                                            paddingLeft: '40px',
                                            borderRadius: '50px',
                                            border: '1px solid #e0e0e0',
                                            backgroundColor: '#fff',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                            height: '45px'
                                        }}
                                    />
                                    <FontAwesomeIcon
                                        icon={faSearch}
                                        className="text-muted position-absolute"
                                        style={{ left: '15px', top: '50%', transform: 'translateY(-50%)' }}
                                    />
                                    {term && (
                                        <FontAwesomeIcon
                                            icon={faTimes}
                                            className="text-muted position-absolute cursor-pointer"
                                            onClick={() => { setTerm(''); setPage(1); }}
                                            style={{ right: '15px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                                        />
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                )}

                {/* Grid Section */}
                {selectedFazenda === '0' ? (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <div className="bg-white rounded-circle shadow-sm d-inline-flex align-items-center justify-content-center p-4">
                                <FontAwesomeIcon icon={faHome} size="3x" className="text-muted opacity-50" />
                            </div>
                        </div>
                        <h4 className="text-secondary">Selecione uma fazenda</h4>
                        <p className="text-muted">Escolha uma fazenda acima para visualizar o rebanho.</p>
                    </div>
                ) : animal && animal.length > 0 ? (
                    <>
                        <Row>
                            {animal.map((item) => (
                                <Col xs={12} md={6} lg={4} className="mb-4" key={item.id}>
                                    <Card className="h-100 border-0 shadow-sm hover-cards" style={{ borderRadius: '16px', overflow: 'hidden', transition: 'all 0.3s ease' }}>
                                        <CardBody className="p-4 d-flex flex-column">

                                            <div className="d-flex align-items-start mb-4">
                                                <div className="flex-grow-1">
                                                    <h5 className="mb-1 font-weight-bold text-dark">
                                                        {item.brinco}
                                                    </h5>
                                                    <div className="d-flex flex-wrap" style={{ gap: '5px' }}>
                                                        <Badge color="light" className="text-secondary border px-2 py-1">
                                                            {getGrupoLabel(item.grupo)}
                                                        </Badge>
                                                        <Badge color={getStatusColor(item.status)} className="px-2 py-1">
                                                            {getStatusLabel(item.status)}
                                                        </Badge>
                                                        {item.data_exclusao && (
                                                            <Badge color="danger" className="px-2 py-1">
                                                                Excluído
                                                            </Badge>
                                                        )}
                                                        {item.data_descarte && (
                                                            <Badge color="warning" className="px-2 py-1">
                                                                Descartado
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-4 flex-grow-1">
                                                <div className="d-flex align-items-center text-secondary mb-2">
                                                    <div style={{ width: '24px', textAlign: 'center' }} className="mr-2">
                                                        <FontAwesomeIcon icon={faBirthdayCake} className="text-muted" />
                                                    </div>
                                                    <span className="small font-weight-bold text-uppercase mr-1">Nascimento:</span>
                                                    <span className="text-dark">
                                                        <Moment format="DD/MM/YYYY">
                                                            {item.data_nascimento}
                                                        </Moment>
                                                    </span>
                                                </div>
                                                {item.data_ultimo_parto && (
                                                    <div className="d-flex align-items-center text-secondary mb-2">
                                                        <div style={{ width: '24px', textAlign: 'center' }} className="mr-2">
                                                            <FontAwesomeIcon icon={faVenusMars} className="text-muted" />
                                                        </div>
                                                        <span className="small font-weight-bold text-uppercase mr-1">Último Parto:</span>
                                                        <span className="text-dark">
                                                            <Moment format="DD/MM/YYYY">
                                                                {item.data_ultimo_parto}
                                                            </Moment>
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="mt-auto pt-3 border-top d-flex justify-content-between align-items-center">
                                                <Button
                                                    color="link"
                                                    className="text-decoration-none p-0 text-muted d-flex align-items-center"
                                                    onClick={(event) => handleClickHistory(event, item.id, item.brinco)}
                                                    title="Histórico"
                                                >
                                                    <div className="bg-light rounded-circle d-flex align-items-center justify-content-center mr-2" style={{ width: '32px', height: '32px' }}>
                                                        <FontAwesomeIcon icon={faClock} size="sm" />
                                                    </div>
                                                    <span className="small font-weight-bold">Histórico</span>
                                                </Button>

                                                <div className="d-flex">
                                                    {activeTab === 'plantel' ? (
                                                        <>
                                                            <Button
                                                                color="primary"
                                                                className="d-flex align-items-center justify-content-center mr-2 rounded-circle shadow-sm border-0"
                                                                style={{ width: '38px', height: '38px', padding: 0, backgroundColor: 'var(--primary-color)' }}
                                                                component='a'
                                                                href={`/animal/${item.id}`}
                                                                title="Editar"
                                                            >
                                                                <FontAwesomeIcon icon={faPen} size="sm" />
                                                            </Button>
                                                            <Button
                                                                color="danger"
                                                                className="d-flex align-items-center justify-content-center rounded-circle shadow-sm border-0"
                                                                style={{ width: '38px', height: '38px', padding: 0, backgroundColor: '#e03131' }}
                                                                onClick={() => confirmDelete(item.id)}
                                                                title="Excluir"
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} size="sm" />
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <Button
                                                            color="success"
                                                            className="d-flex align-items-center justify-content-center rounded-circle shadow-sm border-0"
                                                            style={{ width: '38px', height: '38px', padding: 0 }}
                                                            onClick={() => handleReactivar(item.id)}
                                                            title="Reativar"
                                                        >
                                                            <FontAwesomeIcon icon={faUndo} size="sm" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Row>
                                <Col className="d-flex justify-content-center mt-4 mb-4">
                                    <div className="d-flex align-items-center bg-white shadow-sm rounded-pill px-2 py-1">
                                        <Button
                                            color="link"
                                            className={`rounded-circle text-decoration-none p-0 d-flex align-items-center justify-content-center ${page === 1 ? 'text-muted' : 'text-primary'}`}
                                            style={{ width: '40px', height: '40px' }}
                                            disabled={page === 1}
                                            onClick={() => setPage(page - 1)}
                                        >
                                            <FontAwesomeIcon icon={faChevronLeft} />
                                        </Button>

                                        <span className="mx-3 text-muted font-weight-bold small">
                                            Página {page} de {totalPages}
                                        </span>

                                        <Button
                                            color="link"
                                            className={`rounded-circle text-decoration-none p-0 d-flex align-items-center justify-content-center ${page === totalPages ? 'text-muted' : 'text-primary'}`}
                                            style={{ width: '40px', height: '40px' }}
                                            disabled={page >= totalPages}
                                            onClick={() => setPage(page + 1)}
                                        >
                                            <FontAwesomeIcon icon={faChevronRight} />
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </>
                ) : (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <div className="bg-white rounded-circle shadow-sm d-inline-flex align-items-center justify-content-center p-4">
                                <FontAwesomeIcon icon={faFolder} size="3x" className="text-muted opacity-50" />
                            </div>
                        </div>
                        <h4 className="text-secondary">Nenhum animal encontrado</h4>
                        <p className="text-muted">Não foram encontrados animais para os filtros selecionados.</p>
                        {term && (
                            <Button color="primary" outline onClick={() => { setTerm(''); setPage(1); }} className="mt-2 rounded-pill px-4">
                                Limpar Filtros
                            </Button>
                        )}
                    </div>
                )}
            </Container>

            <ModalLog toggle={toggleHistory} modal={modalHistory} log={log} brinco={brincoLog} />

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)} centered>
                <ModalHeader toggle={() => setDeleteModalOpen(false)} className="border-0 pb-0"></ModalHeader>
                <ModalBody className="text-center pb-4">
                    <div className="mb-3 text-danger bg-light-danger d-inline-block rounded-circle p-3" style={{ backgroundColor: '#ffebee' }}>
                        <FontAwesomeIcon icon={faTrash} size="2x" />
                    </div>
                    <h4 className="mb-2 font-weight-bold">Excluir Animal</h4>
                    <p className="text-muted mb-4">Tem certeza que deseja excluir este animal? Esta ação não pode ser desfeita.</p>
                </ModalBody>
                <ModalFooter className="border-0 pt-0 justify-content-center pb-4">
                    <Button color="light" onClick={() => setDeleteModalOpen(false)} className="mr-2 rounded-pill px-4 font-weight-bold">Cancelar</Button>
                    <Button color="danger" onClick={handleDelete} className="rounded-pill px-4 font-weight-bold shadow-sm">Confirmar Exclusão</Button>
                </ModalFooter>
            </Modal>

            

            <style>{`
                .hover-cards:hover {
                    box-shadow: 0 10px 25px rgba(0,0,0,0.08) !important;
                    transform: translateY(-4px);
                }
                .hover-tab:hover {
                    background-color: #f1f3f5 !important;
                    color: var(--primary-color) !important;
                }
                .nav-link.active {
                    color: white !important;
                }
            `}</style>
        </div>
    );

}
Animal.propTypes = {
    // classes: PropTypes.object.isRequired, // Removed classes prop as it was likely from Material UI v4 higher order component
};
const mapStateToProps = (state) => {
    return state;
}
const connectedAnimalPage = withRouter(connect(mapStateToProps, null, null, {
    pure: false
})(Animal));
export { connectedAnimalPage as Animal };
